import type { UsageMetadata } from '@avisoma/shared/dist/ProcessingData';
import { CollapsibleCard } from './CollapsibleCard';
import { estimateModelUsageCost, formatUsdEstimate } from '../lib/modelUsageCostEstimate';
import { formatNumber } from '../lib/numberFormat';

const DEFAULT_COLLAPSIBLE_STORAGE_KEY = 'documentView.collapsible.modelApiUsage';

const ROWS: { key: keyof UsageMetadata; label: string }[] = [
  { key: 'totalTokenCount', label: 'Total tokens' },
  { key: 'promptTokenCount', label: 'Prompt' },
  { key: 'cachedContentTokenCount', label: 'Cached content' },
  { key: 'responseTokenCount', label: 'Response' },
  { key: 'toolUsePromptTokenCount', label: 'Tool use prompt' },
  { key: 'thoughtsTokenCount', label: 'Thoughts' },
];

function hasAnyTokens(usage: UsageMetadata | undefined): boolean {
  if (!usage) {
    return false;
  }
  return (usage.totalTokenCount ?? 0) > 0 || ROWS.slice(1).some(({ key }) => (usage[key] ?? 0) > 0);
}

/** True when `DocumentModelUsageSummary` would render (any stage has token usage, or a conversion cost is recorded). */
export function hasModelUsageToDisplay(
  pdfParserUsage: UsageMetadata | undefined,
  dataExtractionUsage: UsageMetadata | undefined,
  embeddingsUsage: UsageMetadata | undefined,
  segmentationUsage?: UsageMetadata,
  htmlConversionCostUsd?: number,
): boolean {
  return (
    hasAnyTokens(pdfParserUsage) ||
    hasAnyTokens(dataExtractionUsage) ||
    hasAnyTokens(embeddingsUsage) ||
    hasAnyTokens(segmentationUsage) ||
    htmlConversionCostUsd !== undefined
  );
}

function shouldShowMetricRow(
  key: keyof UsageMetadata,
  seg: UsageMetadata | undefined,
  pdf: UsageMetadata | undefined,
  de: UsageMetadata | undefined,
  emb: UsageMetadata | undefined,
): boolean {
  if (key === 'totalTokenCount') {
    return hasAnyTokens(seg) || hasAnyTokens(pdf) || hasAnyTokens(de) || hasAnyTokens(emb);
  }
  const gemini = (seg?.[key] ?? 0) > 0 || (pdf?.[key] ?? 0) > 0 || (de?.[key] ?? 0) > 0;
  const embedding = (emb?.[key] ?? 0) > 0;
  return gemini || embedding;
}

const thNum = 'px-1.5 py-0.5 text-right text-[11px] font-semibold text-gray-600 dark:text-zinc-400 tabular-nums';
const tdNum = 'px-1.5 py-0.5 text-right text-xs tabular-nums text-gray-900 dark:text-zinc-100';

export type DocumentModelUsageSummaryProps = {
  pdfParserUsage: UsageMetadata | undefined;
  dataExtractionUsage: UsageMetadata | undefined;
  /** DR-4643: the gemini-3-flash-preview part of `dataExtractionUsage` (NIS / Part Movement); prices only. */
  dataExtractionFlashPreviewUsage?: UsageMetadata | undefined;
  embeddingsUsage: UsageMetadata | undefined;
  /** Section-segmentation Gemini usage (the pre-parse heading-detection call). */
  segmentationUsage?: UsageMetadata | undefined;
  /** Recorded PDF→HTML conversion cost (USD). Undefined → no conversion row. */
  htmlConversionCostUsd?: number;
  /** Document page count; used to show estimated USD per 1,000 pages. */
  pageCount?: number;
  /** Card title (default: model API usage for a single document). */
  title?: string;
  /** Key for CollapsibleCard localStorage (default: document view). */
  collapsibleStorageKey?: string;
  /** Shown below the pricing note when usage is non-empty (e.g. organisation-level sum of projects). */
  secondaryFootnote?: string;
};

/**
 * Model usage and estimated cost in one table: Gemini segmentation + PDF parser (priced at
 * `gemini-3-flash-preview`), Gemini data extraction (priced at `gemini-3.5-flash`, except the
 * recorded NIS / Part Movement bucket at `gemini-3-flash-preview`), OpenAI
 * text-embedding-3-large, and, when recorded, the PDF→HTML conversion cost as its own row.
 */
export function DocumentModelUsageSummary({
  pdfParserUsage,
  dataExtractionUsage,
  dataExtractionFlashPreviewUsage,
  embeddingsUsage,
  segmentationUsage,
  htmlConversionCostUsd,
  pageCount,
  title = 'Model API usage (document)',
  collapsibleStorageKey = DEFAULT_COLLAPSIBLE_STORAGE_KEY,
  secondaryFootnote,
}: DocumentModelUsageSummaryProps) {
  if (
    !hasModelUsageToDisplay(
      pdfParserUsage,
      dataExtractionUsage,
      embeddingsUsage,
      segmentationUsage,
      htmlConversionCostUsd,
    )
  ) {
    return null;
  }

  const cost = estimateModelUsageCost({
    segmentation: segmentationUsage,
    pdfParser: pdfParserUsage,
    dataExtraction: dataExtractionUsage,
    dataExtractionFlashPreview: dataExtractionFlashPreviewUsage,
    embeddings: embeddingsUsage,
    htmlConversionCostUsd,
  });
  const grandTotalUsd = cost.total;
  const per1000PagesUsd =
    pageCount !== undefined && pageCount > 0 && Number.isFinite(grandTotalUsd)
      ? (grandTotalUsd / pageCount) * 1000
      : null;

  const summary =
    pageCount !== undefined && pageCount >= 0
      ? `${formatUsdEstimate(grandTotalUsd)} · ${formatNumber(pageCount)} pages`
      : formatUsdEstimate(grandTotalUsd);

  return (
    <CollapsibleCard
      storageKey={collapsibleStorageKey}
      title={title}
      summary={summary}
    >
      <div className="overflow-x-auto rounded border border-gray-200/90 dark:border-zinc-600/80">
        <table className="w-full min-w-[280px] border-collapse text-left">
          <thead>
            <tr className="border-b border-gray-200 bg-white/50 dark:border-zinc-600 dark:bg-zinc-900/30">
              <th scope="col" className="px-1.5 py-1 text-[11px] font-semibold text-gray-500 dark:text-zinc-400">
                Metric
              </th>
              <th scope="col" className={thNum}>
                Segmentation
              </th>
              <th scope="col" className={thNum}>
                PDF parser
              </th>
              <th scope="col" className={thNum}>
                Data extraction
              </th>
              <th scope="col" className={`${thNum} text-emerald-900 dark:text-emerald-100/90`}>
                Embeddings
              </th>
            </tr>
          </thead>
          <tbody className="text-xs">
            {ROWS.map(({ key, label }) => {
              if (!shouldShowMetricRow(key, segmentationUsage, pdfParserUsage, dataExtractionUsage, embeddingsUsage)) {
                return null;
              }
              const sv = segmentationUsage?.[key] ?? 0;
              const pv = pdfParserUsage?.[key] ?? 0;
              const dv = dataExtractionUsage?.[key] ?? 0;
              const ev = embeddingsUsage?.[key] ?? 0;
              return (
                <tr key={key} className="border-b border-gray-100 dark:border-zinc-700/80">
                  <th scope="row" className="px-1.5 py-0.5 text-left font-normal text-gray-600 dark:text-zinc-400">
                    {label}
                  </th>
                  <td className={tdNum}>{formatNumber(sv)}</td>
                  <td className={tdNum}>{formatNumber(pv)}</td>
                  <td className={tdNum}>{formatNumber(dv)}</td>
                  <td className={`${tdNum} text-emerald-950/95 dark:text-emerald-100/90`}>{formatNumber(ev)}</td>
                </tr>
              );
            })}
            <tr className="border-t border-gray-200 bg-gray-100/90 dark:border-zinc-600 dark:bg-zinc-900/60">
              <th
                scope="row"
                className="px-1.5 py-1 text-left text-[11px] font-medium text-gray-700 dark:text-zinc-200"
              >
                Est. cost
              </th>
              <td className={`${tdNum} font-semibold text-indigo-950 dark:text-indigo-100`}>
                {formatUsdEstimate(cost.segmentation)}
              </td>
              <td className={`${tdNum} font-semibold text-indigo-950 dark:text-indigo-100`}>
                {formatUsdEstimate(cost.pdfParser)}
              </td>
              <td className={`${tdNum} font-semibold text-indigo-950 dark:text-indigo-100`}>
                {formatUsdEstimate(cost.dataExtraction)}
              </td>
              <td className="px-1.5 py-1 text-right text-sm font-bold tabular-nums text-emerald-900 dark:text-emerald-100">
                {formatUsdEstimate(cost.embeddings)}
              </td>
            </tr>
            {htmlConversionCostUsd !== undefined && (
              <tr className="border-t border-gray-200 bg-gray-100/90 dark:border-zinc-600 dark:bg-zinc-900/60">
                <th
                  scope="row"
                  className="px-1.5 py-1 text-left text-[11px] font-medium text-gray-700 dark:text-zinc-200"
                >
                  PDF→HTML conversion
                </th>
                <td colSpan={4} className={`${tdNum} font-semibold text-indigo-950 dark:text-indigo-100`}>
                  {formatUsdEstimate(cost.htmlConversion)}
                </td>
              </tr>
            )}
            <tr className="border-t-2 border-violet-300/90 bg-violet-50/95 dark:border-violet-700/80 dark:bg-violet-950/45">
              <th
                scope="row"
                className="px-1.5 py-1.5 text-left text-[11px] font-semibold text-violet-950 dark:text-violet-100"
              >
                Grand total (est.)
              </th>
              <td
                colSpan={4}
                className="px-1.5 py-1.5 text-right text-base font-bold tabular-nums text-violet-950 dark:text-violet-50"
              >
                {formatUsdEstimate(grandTotalUsd)}
              </td>
            </tr>
            {per1000PagesUsd !== null && (
              <tr className="border-t border-violet-200/80 bg-violet-50/70 dark:border-violet-800/60 dark:bg-violet-950/35">
                <th
                  scope="row"
                  className="px-1.5 py-1 text-left text-[11px] font-medium text-violet-900/95 dark:text-violet-200/95"
                >
                  Est. per 1,000 pages
                </th>
                <td
                  colSpan={4}
                  className="px-1.5 py-1 text-right text-sm font-semibold tabular-nums text-violet-950 dark:text-violet-100"
                >
                  {formatUsdEstimate(per1000PagesUsd)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] leading-snug text-gray-500 dark:text-zinc-500">
        <span className="font-medium text-gray-600 dark:text-zinc-400">Gemini</span> (Standard): section segmentation and
        PDF parser at <code className="text-[11px]">gemini-3-flash-preview</code>, data extraction at{' '}
        <code className="text-[11px]">gemini-3.5-flash</code>, except NIS Statement and Part Movement extraction at{' '}
        <code className="text-[11px]">gemini-3-flash-preview</code> where recorded.{' '}
        <span className="font-medium text-gray-600 dark:text-zinc-400">Embeddings</span> (
        <code className="text-[11px]">text-embedding-3-large</code>): input tokens, summed per page (chunked pages =
        multiple API calls). <span className="font-medium text-gray-600 dark:text-zinc-400">PDF→HTML conversion</span>:
        from the converter&apos;s per-page record, counted once per page&apos;s current conversion; pages extracted before
        this was recorded show it after re-extraction. Estimates use public list prices; actual billing may differ.
      </p>
      {secondaryFootnote ? (
        <p className="mt-2 text-[11px] leading-snug text-gray-500 dark:text-zinc-500">{secondaryFootnote}</p>
      ) : null}
    </CollapsibleCard>
  );
}
